import { sort, readOnly } from '@ember/object/computed';
import { computed } from '@ember/object';
import { A } from '@ember/array';
import Service, { inject as service } from '@ember/service';
import { initializePropertyDefaults } from 'sh-www/utils/initialize-state-properties';
import { ICON_SORTS } from "../../../../constants/lsg";

export default Service.extend({
  /**
   * Register Services
   */
  store: service(),
  moment: service(),

  /**
   * @inheritDoc
   */
  propertyDefaults: {
    /**
     * @type {Assignment|null}      The current assignment in the blaster view.
     */
    currentAssignment: null,

    /**
     * @type {String}     Optional custom message to send along with the blast.
     */
    customMessage: '',

    /**
     * @type {FreelancerSearchResultCollection|null}    The current collection of freelancer results
     */
    freelancerBlastSearchResultCollection: null,

    /**
     * @type {Boolean}      True when loading the blaster results.
     */
    isLoadingBlastResults: false,

    /**
     * @type {Boolean}      True when we're loading all assignment in the blaster select dropdown.
     */
    isLoadingAllAssignments: true,

    /**
     * @type {Boolean}    True if we are loading our initial state for the blaster form.
     */
    isLoadingInitialState: true,

    /**
     * @type {Boolean}    True if select all good results is checked
     */
    isSelectAllResultsChecked: false,

    /**
     * @type {Boolean}    True if we are sending a blast request
     */
    isSendingBlast: false,

    /**
     * This is a handler for a transition when a new assignment has been selected.
     *
     * @type {function(*)}
     */
    onNewPostingSelectedTransition: () => {
      return (assignmentId) => {
        throw new Error('no new posting selected transition defined');
      };
    },

    /**
     * The current sort name for the 'filtered' results
     *
     * @type {String} sortName     ADMIN_BLASTER__SORT_NAMES.*
     */
    sortFilteredResultsName: '',

    /**
     * The direct of the sort for 'filtered' results
     *
     * @type {String}     ICON_SORTS.*
     */
    sortFilteredResultsDirection: ICON_SORTS.SORT_DESCENDING,

    /**
     * The current sort name for the normal 'good' results
     *
     * @type {String}     ADMIN_BLASTER__SORT_NAMES.*
     */
    sortResultsName: '',

    /**
     * The direct of the sort for 'good' results
     *
     * @type {String}     ICON_SORTS.*
     */
    sortResultsDirection: ICON_SORTS.SORT_DESCENDING,

    /**
     * Blast distance user can set for extending radius of search results.
     *
     * @type {Number}     of type ADMIN_BLASTER__DISTANCES_AVAILABLE
     */
    updatedBlastDistance: 200,

    /**
     * @type {Location|null}    The city Location model selected by the user or null
     */
    updatedCityLocation: null,

    /**
     * @type {Ember.MutableArray<Object>}   Array of the option items that have the country names and ids.
     */
    updatedCountryOptions: () => A(),

    updatedEquipmentOptions: [],

    updatedLanguageOptions: [],

    /**
     * @type {Boolean}   Boolean indicating whether the search is restricted to the borders of the locations specified
     */
    updatedCountryRestricted: false,

    /**
     * @type {Ember.A<String>}    Array of all select blast result ids to send the blast for.
     */
    updatedSelectedBlastResultIds: () => A(),

    /**
     * @type {Number}     The minimum rating the admin has selected for the blast.
     */
    updatedSelectedMinRating: 3,

    /**
     * @type {Number} User Select status
     */
    updatedSelectStatus: null,

    /**
     * @type {Ember.A<Number>}    These are the service ids that the admin has selected to filter by.
     */
    updatedSelectedServiceIds: () => A(),

    /**
     * @type {String}    
     */
     updatedServiceSearchType: 'or',
  },

  /**
   * @type {Ember.A<Assignment>}    Array of active postings that are available to blast.
   */
  activePostings: A(),

  /**
   * @type {Boolean}      True if the form elements should be disabled.
   */
  areFormElementsDisabled: computed(
  'isLoadingBlastResults',
  'isLoadingInitialState',
  'isSendingBlast',
  function() {
    return this.get('isLoadingInitialState') ||
      this.get('isLoadingBlastResults') ||
      this.get('isSendingBlast');
  }),

  /**
   * @type {Ember.MutableArray<Country>}    All available countries loaded from the server.
   */
  availableCountries: A(),

  availableEquipment: [],

  availableLanguages: [],

  /**
   * @type {Ember.MutableArray<Region>}    All available regions loaded from the server.
   */
  availableRegions: A(),

  /**
   * @type {Ember.MutableArray<Service>}    All available Services loaded from the server.
   */
  availableServices: A(),

  /**
   * @type {Ember.A<Blast>}     Array of the blasts we will show in the blast history section.
   */
  blastHistoryItems: A(),

  /**
   * Results that have been filtered out
   *
   * @type {Ember.A<FreelancerSearchResult>}
   */
  blastFilteredResults: computed('freelancerBlastSearchResultCollection.id', function() {
    return this.get('freelancerBlastSearchResultCollection.filteredResults');
  }),

  /**
   * Sort for the blast results.
   *
   * @type {Array<String>}
   */
  blastFilteredResultsSorting: computed('blastFilteredResults.length', 'sortFilteredResultsName', 'sortFilteredResultsDirection', function() {
    let sortName = this.get('sortFilteredResultsName'),
      sortDirection = this.get('sortFilteredResultsDirection');

    if (!sortName) {
      return ['id'];
    }

    return sortDirection === ICON_SORTS.SORT_ASCENDING ?
      [sortName] :
      [`${sortName}:${sortDirection}`];
  }),

  /**
   * Valid blast results sorted by the 'field' and the sort 'direction'
   *
   * @type {Ember.A<FreelancerSearchResult>}
   */
  blastFilteredResultsSorted: sort('blastFilteredResults', 'blastFilteredResultsSorting'),

  /**
   * Valid blast results
   *
   * @type {Ember.A<FreelancerSearchResult>}
   */
  blastResults: computed('freelancerBlastSearchResultCollection.id', function() {
    return this.get('freelancerBlastSearchResultCollection.results');
  }),

  /**
   * Sort for the blast results.
   *
   * @type {Array<String>}
   */
  blastResultsSorting: computed('blastResults.length', 'sortResultsName', 'sortResultsDirection', function() {
    let sortName = this.get('sortResultsName'),
      sortDirection = this.get('sortResultsDirection');

    if (!sortName) {
      return ['id'];
    }

    return sortDirection === ICON_SORTS.SORT_ASCENDING ?
      [sortName] :
      [`${sortName}:${sortDirection}`];
  }),

  /**
   * Valid blast results sorted by the 'field' and the sort 'direction'
   *
   * @type {Ember.A<FreelancerSearchResult>}
   */
  blastResultsSorted: sort('blastResults', 'blastResultsSorting'),

  /**
   * @type {Number|null}     The total count of good blast results from the last search.
   */
  blastTotalGoodResults: readOnly('freelancerBlastSearchResultCollection.totalCount'),

  /**
   * @type {Boolean}    True when the blast button should be Enabled.
   */
  isBlastButtonEnabled: computed('isSendingBlast', 'updatedSelectedBlastResultIds.length', function() {
    if (this.get('isSendingBlast')) {
      return false;
    }

    return this.get('updatedSelectedBlastResultIds.length');
  }),

  /**
   * @type {Boolean}    True when the blaster button should be enabled.
   */
  isSearchBlastResultsEnabled: computed(
  'isSendingBlast',
  'isLoadingBlastResults',
  function() {
    if (this.get('isLoadingBlastResults') || this.get('isSendingBlast')) {
      return false;
    }

    return true;
  }),

  /**
   * This is a map of blastResult ids => true.  i.e. a quick way to check to see if a blast result is selected.
   *
   * @type {Object}
   */
  updatedSelectedBlastResultsMap: computed('updatedSelectedBlastResultIds.length', function() {
    let selectedResultIds = this.get('updatedSelectedBlastResultIds'),
      selectedResultsIdsMap = {};

    selectedResultIds.reduce((map, resultId) => {
      map[resultId] = true;
      return map;
    }, selectedResultsIdsMap);

    return selectedResultsIdsMap;
  }),

  /**
   * @type {Ember.MutableArray<Country>}    Array of countries objects that were selected.
   */
  updatedSelectedCountries: computed('updatedCountryOptions.length', function() {
    let selectedCountries = A();
    this.get('updatedCountryOptions').forEach(contryOption => {
      selectedCountries.push(this.get('store').peekRecord('country', contryOption.value));
    });

    return selectedCountries;
  }),

  /**
   * This is a dictionary with the key being the service id and the value is 'true' when this service id has been
   * selected.
   *
   * @type {Object}
   */
  updatedSelectedServicesMap: computed('updatedSelectedServiceIds.length', function() {
    let selectedServiceIds = this.get('updatedSelectedServiceIds'),
      selectedServicesMap = {};

    selectedServiceIds.forEach(serviceId => {
      selectedServicesMap[serviceId] = true;
    });

    return selectedServicesMap;
  }),

  /**
   * These are the options representing a checkbox state for each service.
   *
   * [{
   *    title: serviceName
   *    value: serviceId
   *    selected: boolean - true when this service has been selected by the admin
   *  },{
   *    ...
   *  }]
   *
   * @type {Ember.A<Object>}
   */
  updatedServiceOptions: computed('updatedSelectedServiceIds.length', 'updatedSelectedServicesMap', function() {
    let selectedServicesMap = this.get('updatedSelectedServicesMap'),
      availableServices = this.get('availableServices'),
      selectedServiceOptions = A();

    availableServices.forEach(service => {
      selectedServiceOptions.pushObject({
        title: service.get('name'),
        value: service.get('id'),
        selected: selectedServicesMap[service.get('id')]
      });
    });

    return selectedServiceOptions;
  }),

  /**
   * @inheritDoc
   */
  init() {
    this._super(...arguments);
    initializePropertyDefaults(this);
  }
});
